import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Robot, Lightning, Warning, Question, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Phase 10.2D — Internal AI Quality Monitor panel (per-lead).
 *
 * Renders inside CrmLeadDetail. Analysis is INTERNAL-ONLY:
 *  - never sends a customer-facing message
 *  - never mutates automation
 *  - only stores QA insights against the lead for Ops review
 */
export default function WhatsAppQaPanel({ leadId }) {
  const [latest, setLatest] = useState(null);
  const [storedAt, setStoredAt] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = () => {
    if (!leadId) return;
    setLoading(true);
    api.get(`/admin/crm-leads/${leadId}/qa-analysis`)
      .then(r => {
        setLatest(r.data?.latest || null);
        setStoredAt(r.data?.stored_at || null);
        setHistoryCount(r.data?.history_count || 0);
      })
      .catch(err => { console.error("qa-analysis fetch failed", err); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [leadId]);

  const runAnalysis = () => {
    if (!leadId) return;
    setRunning(true);
    api.post(`/admin/crm-leads/${leadId}/analyze-conversation`)
      .then(r => {
        const d = r.data || {};
        if (d.ok) {
          setLatest(d);
          setStoredAt(d.generated_at);
          setHistoryCount(c => c + 1);
          toast.success("AI QA analysis complete");
        } else {
          toast.error(`Analysis failed: ${d.error || "unknown"}`);
        }
      })
      .catch(err => {
        toast.error(err?.response?.data?.detail || "Analysis request failed");
      })
      .finally(() => setRunning(false));
  };

  return (
    <div data-testid="wa-qa-panel" className="rounded-md border border-[#2A2A2A] bg-[#0D0D0D]/70 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Robot size={16} weight="bold" className="text-amber-400" />
            <div className="text-[11px] tracking-widest uppercase font-bold text-amber-400">
              AI Quality Monitor
            </div>
            <span className="text-[9px] text-white/40 uppercase tracking-widest">Internal · Read-only</span>
          </div>
          <div className="text-[10px] text-white/40 mt-1">
            Analyzes WhatsApp conversation for QA insights. Does not message the customer.
          </div>
        </div>
        <button
          type="button"
          onClick={runAnalysis}
          disabled={running || !leadId}
          data-testid="wa-qa-run"
          className="text-[10px] tracking-widest uppercase font-bold py-2 px-3 border border-amber-500 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          <ArrowsClockwise size={12} weight="bold" />
          {running ? "Analyzing…" : "Analyze WhatsApp Conversation"}
        </button>
      </div>

      {loading && <div className="text-[11px] text-white/50">Loading QA analysis…</div>}

      {!loading && !latest && (
        <div data-testid="wa-qa-empty" className="text-[11px] text-white/50 italic">
          No analysis yet. Click <span className="text-amber-300">Analyze WhatsApp Conversation</span> to run one.
        </div>
      )}

      {!loading && latest && (
        <div data-testid="wa-qa-result" className="space-y-3">
          <div className="flex flex-wrap gap-2 text-[10px]">
            <Chip data-testid="wa-qa-quality" label="Quality" value={latest.lead_quality} tone={qualityTone(latest.lead_quality)} />
            <Chip data-testid="wa-qa-intent" label="Intent" value={latest.customer_intent} tone={intentTone(latest.customer_intent)} />
            <Chip data-testid="wa-qa-status" label="Status" value={latest.conversation_status} tone={statusTone(latest.conversation_status)} />
            <Chip data-testid="wa-qa-action" label="Action" value={latest.recommended_admin_action} tone="neutral" />
            <Chip label="Confidence" value={latest.confidence} tone="neutral" />
          </div>

          {latest.drop_off_point && (
            <Row icon={<Warning size={12} weight="bold" className="text-amber-400" />} testid="wa-qa-dropoff"
                 title="Drop-off point" value={latest.drop_off_point} />
          )}

          {latest.missing_fields?.length > 0 && (
            <Row icon={<Question size={12} weight="bold" className="text-white/60" />} testid="wa-qa-missing"
                 title={`Missing fields (${latest.missing_fields.length})`}
                 value={latest.missing_fields.join(", ")} />
          )}

          {latest.risk_flags?.length > 0 && (
            <div data-testid="wa-qa-risks">
              <div className="text-[9px] uppercase tracking-widest text-white/40 mb-1">Risk flags</div>
              <div className="flex flex-wrap gap-1.5">
                {latest.risk_flags.map(f => (
                  <span key={f} data-testid={`wa-qa-risk-${f}`}
                        className="text-[10px] px-2 py-0.5 border border-red-500/60 bg-red-500/10 text-red-300 rounded-sm">
                    <Lightning size={10} weight="bold" className="inline mr-1" />{f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(latest.bot_issue_summary || latest.staff_issue_summary) && (
            <div className="space-y-1.5 text-[11px]">
              {latest.bot_issue_summary && (
                <div data-testid="wa-qa-bot-issue" className="text-white/70">
                  <span className="text-white/40 uppercase tracking-widest text-[9px] mr-1">Bot:</span>
                  {latest.bot_issue_summary}
                </div>
              )}
              {latest.staff_issue_summary && (
                <div data-testid="wa-qa-staff-issue" className="text-white/70">
                  <span className="text-white/40 uppercase tracking-widest text-[9px] mr-1">Staff:</span>
                  {latest.staff_issue_summary}
                </div>
              )}
            </div>
          )}

          {latest.recommended_process_improvement && (
            <div data-testid="wa-qa-process" className="text-[11px] text-white/80 border-l-2 border-amber-500/60 pl-2">
              <span className="text-[9px] uppercase tracking-widest text-amber-400 mr-1">Process improvement</span>
              <div>{latest.recommended_process_improvement}</div>
            </div>
          )}

          {latest.suggested_better_question && (
            <div data-testid="wa-qa-wording" className="text-[11px] text-white/80 border-l-2 border-blue-500/60 pl-2">
              <span className="text-[9px] uppercase tracking-widest text-blue-400 mr-1">Wording suggestion (Ops review only)</span>
              <div className="italic">"{latest.suggested_better_question}"</div>
            </div>
          )}

          {latest.captured_fields_summary && Object.keys(latest.captured_fields_summary).length > 0 && (
            <details data-testid="wa-qa-captured" className="text-[11px]">
              <summary className="text-white/40 uppercase tracking-widest text-[9px] cursor-pointer">Captured fields ({Object.keys(latest.captured_fields_summary).length})</summary>
              <pre className="mt-1 text-white/60 text-[10px] bg-black/30 p-2 rounded overflow-x-auto">{JSON.stringify(latest.captured_fields_summary, null, 2)}</pre>
            </details>
          )}

          <div className="text-[9px] text-white/30 uppercase tracking-widest pt-2 border-t border-[#1a1a1a]">
            Last analysis: {storedAt || "—"} · History: {historyCount} run{historyCount === 1 ? "" : "s"}
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ label, value, tone = "neutral", ...rest }) {
  const tones = {
    hot:     "border-red-500 text-red-300 bg-red-500/10",
    warm:    "border-amber-500 text-amber-300 bg-amber-500/10",
    cold:    "border-blue-500 text-blue-300 bg-blue-500/10",
    good:    "border-emerald-500 text-emerald-300 bg-emerald-500/10",
    warn:    "border-amber-500 text-amber-300 bg-amber-500/10",
    danger:  "border-red-500 text-red-300 bg-red-500/10",
    neutral: "border-[#2A2A2A] text-white/70 bg-white/5",
  };
  return (
    <span {...rest} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm ${tones[tone] || tones.neutral}`}>
      <span className="text-white/40 mr-1">{label}:</span>
      {value || "—"}
    </span>
  );
}

function Row({ title, value, icon, testid }) {
  return (
    <div data-testid={testid} className="flex items-start gap-1.5 text-[11px]">
      {icon}
      <div>
        <span className="text-white/40 uppercase tracking-widest text-[9px] mr-1">{title}</span>
        <span className="text-white/80">{value}</span>
      </div>
    </div>
  );
}

function qualityTone(q) { return ({ hot: "hot", warm: "warm", cold: "cold", junk: "danger" })[q] || "neutral"; }
function intentTone(i)  { return ({ selling: "good", sold: "warn", wants_call: "warn", confused: "warn", not_interested: "cold", price_checking: "warn" })[i] || "neutral"; }
function statusTone(s)  { return ({ completed: "good", stuck: "danger", needs_admin_review: "warn", stopped: "danger", no_response: "warn" })[s] || "neutral"; }
